import { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { Grid } from '@mui/system';
import { isEmpty, isNil } from 'lodash';
import { MarketComparisonWritable } from 'api';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import {
  FUND_INVESTMENT_MARKET_ADJUSTMENT_REFERENCE_TITLE,
  FUND_INVESTMENT_MARKET_ADJUSTMENT_REFERENCE_TYPE,
} from 'common/constants/documents';
import {
  FUND_INVESTMENT_MARKET_ADJUSTMENT_PAGE_KEY,
  FUND_INVESTMENT_MARKET_ADJUSTMENT_PAGE_VALUE,
} from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { ScalarSpreadsheetProps, UseWorkbookValues } from 'common/types/scalarSpreadsheet';
import { ExcelExportContextValues, UseStoreValues } from 'common/types/store';
import { CustomContainer, CustomScalarSpreadsheet, EmptyTableMessage, SectionHeading } from 'components';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useFundInvestmentContext } from 'context';
import ExcelExportContext from 'context/ExcelExportContext';
import {
  FundInvestmentMarketAdjustmentFieldAttributes,
  MarketAdjustmentFieldAttributes,
  MarketComparisonFieldAttributes,
} from 'pages/FundInvestments';
import {
  FUND_INVESTMENTS_CONTAINER_CLASSNAME,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_CONTAINER_CLASSNAME,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_FIELD_ATTRIBUTES,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EMPTY_TERMS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE,
  FUND_INVESTMENTS_MARKET_COMPARISON_FIELD_ATTRIBUTES,
  FUND_INVESTMENTS_TITLE,
} from 'pages/FundInvestments/common/constants';
import {
  useFundInvestmentLayout,
  useFundInvestmentMarketAdjustmentActions,
  useFundInvestmentMarketAdjustmentSheet,
  useFundInvestmentMarketAdjustmentState,
  useFundInvestmentMarketAdjustmentSync,
} from 'pages/FundInvestments/hooks';
import { ComparisonByDate, getComparisonsByDate, getUniqueComparisons } from 'pages/FundInvestments/utils';
import AddGpcDialog from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog';
import MarketDateDialog from 'pages/ValuationsAllocation/components/MarketDateDialog/MarketDateDialog';
import { TickersSymbolsByDate } from 'pages/ValuationsAllocation/types';
import { getSelectionCellOptions, SpecificApproach } from 'pages/ValuationsAllocation/util';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import { useAllocationValuesStore } from 'store';
import { dbShortDate, getArrayValue, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import {
  CompanyTypesPresence,
  MarketAdjustment,
  SetAndSynchronizeMarkedAdjustmentParams,
} from './FundInvestmentMarketAdjustment.types';

const FundInvestmentMarketAdjustment: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { fieldAttributes } = getObjectValue(storeValue);
  const { setCurrentSelectedValues } = useContext(ExcelExportContext) as unknown as ExcelExportContextValues;

  // Documents
  const { addReferenceForExistingDocuments, filesToSave, setFilesToSave } = useDocuments();

  // Notes
  const { notes, notesHasChanged, onAddNote, onDeleteNote, onUpdateNotes, saveNotes, setNotes } = useNotes();

  // Allocation store
  const currentAllocationId = useAllocationValuesStore(state => state.currentAllocationId);

  // Fund investments context
  const { fundInvestmentAllocationStatus, fundInvestmentSpreadsheet, fundInvestmentValuationAllocation } =
    useFundInvestmentContext();
  const {
    isFetchingFundInvestmentByAllocation,
    isFetchingPublicCompsByDate,
    isFetchingValuationByAllocation,
    isLoadingFundInvestmentByAllocation,
    isLoadingValuationByAllocation,
    isMarketDialogOpen,
    isUpdatingFundInvestmentDetails,
    isUpdatingValuation,
    marketCurrency,
    marketDate,
    selectedMeasurementDate,
    setIsMarketDialogOpen,
    setPublicCompsIndexesByDateParams,
  } = fundInvestmentValuationAllocation;
  const {
    currentAsOfDate,
    currentMarketAdjustment,
    isValidating,
    setArePendingChanges,
    setCurrentAsOfDate,
    setCurrentMarketAdjustment,
    setCurrentMarketComparisons,
  } = fundInvestmentSpreadsheet;
  const { isCurrentValuationDisabled } = fundInvestmentAllocationStatus;

  // Market adjustment state
  const {
    currentPercentileSelection,
    fallbackUniqueComparisonsRef,
    marketAdjustmentForSheet,
    marketAdjustmentRef,
    marketComparisonsForSheet,
    marketComparisonsRef,
    openAddComparableDialog,
    prevAsOfDateRef,
    prevMarketCurrencyRef,
    prevMarketDateRef,
    propertyUpdated,
    setCurrentPercentileSelection,
    setMarketAdjustmentForSheet,
    setMarketComparisonsForSheet,
    setOpenAddComparableDialog,
    setPropertyUpdated,
  } = useFundInvestmentMarketAdjustmentState();

  const currentComparisonsByDate = useMemo<ComparisonByDate[]>(
    () =>
      getComparisonsByDate({
        asOfDate: getStringValue(currentAsOfDate),
        comparisons: marketComparisonsForSheet,
        marketDate,
      }),
    [currentAsOfDate, marketComparisonsForSheet, marketDate]
  );

  const currentUniqueComparisons = useMemo<MarketComparisonWritable[]>(() => {
    const uniqueComparisons = getUniqueComparisons({
      comparisons: marketComparisonsForSheet,
      date: marketDate,
      fallback: fallbackUniqueComparisonsRef.current,
    });

    fallbackUniqueComparisonsRef.current = uniqueComparisons;

    return uniqueComparisons;
  }, [fallbackUniqueComparisonsRef, marketComparisonsForSheet, marketDate]);

  const isSingleComparison = useMemo<boolean>(() => currentUniqueComparisons.length === 1, [currentUniqueComparisons]);

  const currentAdjustmentOptions = useMemo<string[]>(
    () =>
      getArrayValue(
        getSelectionCellOptions({
          isMarketAdjustment: true,
          isSingleComparison,
          specificApproach: currentPercentileSelection as SpecificApproach,
        })
      ),
    [currentPercentileSelection, isSingleComparison]
  );

  const currentCompanyTypesPresence = useMemo<CompanyTypesPresence>(() => {
    const marketComparisonPublicCompanies = getArrayValue(currentUniqueComparisons).filter(
      comparison => !comparison.is_index
    );
    const marketComparisonIndexEtf = getArrayValue(currentUniqueComparisons).filter(comparison => comparison.is_index);

    const publicCompaniesCount = getNumberValue(marketComparisonPublicCompanies.length);
    const indexEtfCount = getNumberValue(marketComparisonIndexEtf.length);

    return {
      hasIndexEtf: Boolean(indexEtfCount),
      hasPublicCompany: Boolean(publicCompaniesCount),
      indexEtfCount,
      publicCompaniesCount,
    };
  }, [currentUniqueComparisons]);

  const areComparisonsSynchronized = useMemo<boolean>(() => {
    const uniqueComparisonsCount = currentUniqueComparisons.length;
    const comparisonsByDateCount = currentComparisonsByDate.length;
    const marketComparisonsCount = getArrayValue(marketComparisonsForSheet).length;

    // Expect 2 rows (asOfDate and asOfMarketDate) per merged comparison
    const expectedMergedCount = marketComparisonsCount / 2;

    const hasCompleteAsOfDates = currentComparisonsByDate.every(comparison => comparison.as_of_date);
    const hasCompleteAsOfMarketDate = currentComparisonsByDate.every(comparison => comparison.as_of_market_date);

    const hasNoComparisons = [
      uniqueComparisonsCount,
      comparisonsByDateCount,
      marketComparisonsCount,
      expectedMergedCount,
    ].every(count => count === 0);

    const hasSynchronizedComparisonData =
      !isEmpty(currentComparisonsByDate) && hasCompleteAsOfDates && hasCompleteAsOfMarketDate;

    const isComparisonDataSynchronized = hasNoComparisons || hasSynchronizedComparisonData;

    return (
      uniqueComparisonsCount === comparisonsByDateCount &&
      comparisonsByDateCount === expectedMergedCount &&
      isComparisonDataSynchronized
    );
  }, [currentUniqueComparisons, currentComparisonsByDate, marketComparisonsForSheet]);

  const marketAdjustmentFieldAttributes = useMemo<FundInvestmentMarketAdjustmentFieldAttributes>(
    () => ({
      marketAdjustment: getObjectValue(
        fieldAttributes?.[FUND_INVESTMENTS_MARKET_ADJUSTMENT_FIELD_ATTRIBUTES]
      ) as MarketAdjustmentFieldAttributes,
      marketComparison: getObjectValue(
        fieldAttributes?.[FUND_INVESTMENTS_MARKET_COMPARISON_FIELD_ATTRIBUTES]
      ) as MarketComparisonFieldAttributes,
    }),
    [fieldAttributes]
  );

  const isLoading = useMemo<boolean>(
    () =>
      isLoadingValuationByAllocation ||
      isLoadingFundInvestmentByAllocation ||
      isNil(currentAllocationId) ||
      isNil(currentAsOfDate) ||
      isNil(marketDate) ||
      isEmpty(currentAdjustmentOptions) ||
      isEmpty(marketAdjustmentFieldAttributes),
    [
      currentAdjustmentOptions,
      currentAllocationId,
      currentAsOfDate,
      isLoadingFundInvestmentByAllocation,
      isLoadingValuationByAllocation,
      marketAdjustmentFieldAttributes,
      marketDate,
    ]
  );

  const setSynchronizedMarketAdjustment = useCallback(
    (params: SetAndSynchronizeMarkedAdjustmentParams) => {
      const { asOfDate, percentileSelection } = params;

      const updatedMarketAdjustment = getObjectValue({
        ...marketAdjustmentRef.current,
      } as MarketAdjustment);

      // Update as of date and refetch public comps by date
      if (!isNil(asOfDate)) {
        const formattedAsOfDate = getStringValue(dbShortDate(asOfDate));

        const currentIndexesTickerSymbols = getArrayValue(currentUniqueComparisons)
          .filter(comparison => comparison.is_index)
          .map(comparison => getStringValue(comparison.ticker_symbol))
          .filter(Boolean);

        const currentPublicCompsTickerSymbols = getArrayValue(currentUniqueComparisons)
          .filter(comparison => !comparison.is_index)
          .map(comparison => getStringValue(comparison.ticker_symbol))
          .filter(Boolean);

        const currentIndexesTickerSymbolsByDate: TickersSymbolsByDate = {
          [formattedAsOfDate]: currentIndexesTickerSymbols,
        };

        const currentPublicCompsTickerSymbolsByDate: TickersSymbolsByDate = {
          [formattedAsOfDate]: currentPublicCompsTickerSymbols,
        };

        setPropertyUpdated('asOfDate');

        setPublicCompsIndexesByDateParams({
          currency: marketCurrency,
          tickersSymbolsByDateAndType: {
            indexesByDate: currentIndexesTickerSymbolsByDate,
            publicCompsByDate: currentPublicCompsTickerSymbolsByDate,
          },
        });

        setCurrentAsOfDate(asOfDate);
      }

      // Update percentile selection
      if (!isNil(percentileSelection)) {
        const { percentile_selection_a: percentileSelectionA, percentile_selection_b: percentileSelectionB } =
          getObjectValue(percentileSelection);

        updatedMarketAdjustment.percentile_selection_a = percentileSelectionA;
        updatedMarketAdjustment.percentile_selection_b = percentileSelectionB;

        setCurrentPercentileSelection(percentileSelection);
      }

      // Update market adjustment for sheet
      setMarketAdjustmentForSheet(updatedMarketAdjustment);
    },
    [
      currentUniqueComparisons,
      marketAdjustmentRef,
      marketCurrency,
      setCurrentAsOfDate,
      setCurrentPercentileSelection,
      setMarketAdjustmentForSheet,
      setPropertyUpdated,
      setPublicCompsIndexesByDateParams,
    ]
  );

  // Scalar spreadsheet
  const { spreadsheet: fundInvestmentMarketAdjustmentSheet } = useFundInvestmentMarketAdjustmentSheet({
    adjustmentOptions: currentAdjustmentOptions,
    areComparisonsSynchronized,
    asOfDate: getStringValue(currentAsOfDate),
    companyTypesPresence: currentCompanyTypesPresence,
    comparisonsByDate: currentComparisonsByDate,
    fieldAttributes: marketAdjustmentFieldAttributes,
    forceReadOnly:
      isCurrentValuationDisabled ||
      isFetchingPublicCompsByDate ||
      isUpdatingFundInvestmentDetails ||
      isUpdatingValuation,
    isSingleComparison,
    marketAdjustment: marketAdjustmentForSheet,
    marketCurrency,
    marketDate,
    percentileSelection: currentPercentileSelection,
    setMarketAdjustment: setCurrentMarketAdjustment,
    setMarketComparisons: setCurrentMarketComparisons,
    setSynchronizedMarketAdjustment,
    uniqueComparisons: currentUniqueComparisons,
  });

  useEffect(() => {
    if (currentMarketAdjustment?.id) {
      setCurrentSelectedValues({
        market_adjustment: currentMarketAdjustment.id,
      });
    }
  }, [currentMarketAdjustment, setCurrentSelectedValues]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: create global Market adjustment SpreadsheetConfig type
  const spreadsheets = useMemo<SpreadsheetConfig[]>(
    () => (fundInvestmentMarketAdjustmentSheet?.yLength ? [fundInvestmentMarketAdjustmentSheet] : []),
    [fundInvestmentMarketAdjustmentSheet]
  );

  const { areCellsValid, evaluateSpreadsheets, onChange, workbook, workbookContextValue } = useWorkbook(
    spreadsheets
  ) as unknown as UseWorkbookValues;

  const [format, formatDispatch] = useFormat({
    marketDate,
    measurementDate: selectedMeasurementDate,
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: marketCurrency,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  // Market adjustment actions
  const { addComparison, removeComparison, saveMarketAdjustment, saveMarketDate } =
    useFundInvestmentMarketAdjustmentActions({
      currentUniqueComparisons,
      evaluateSpreadsheets,
      marketAdjustmentFieldAttributes,
      marketAdjustmentRef,
      marketComparisonsRef,
      notes,
      notesHasChanged,
      prevMarketCurrencyRef,
      prevMarketDateRef,
      saveNotes,
      setMarketComparisonsForSheet,
      setPropertyUpdated,
      spreadsheets,
      filesToSave,
      addReferenceForExistingDocuments,
      selectedMeasurementDate: selectedMeasurementDate!,
    });

  const fundInvestmentMarketAdjustmentSheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (fundInvestmentMarketAdjustmentSheet) {
      return {
        ...fundInvestmentMarketAdjustmentSheet,
        allowDeleteRow: !isCurrentValuationDisabled, // Allow deleting only if not disabled
        data: getArrayValue(fundInvestmentMarketAdjustmentSheet?.data),
        deleteRowFn: removeComparison,
        format,
        formatDispatch,
        onChange,
        sheet: fundInvestmentMarketAdjustmentSheet,
        workbook,
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [
    format,
    formatDispatch,
    fundInvestmentMarketAdjustmentSheet,
    isCurrentValuationDisabled,
    onChange,
    removeComparison,
    workbook,
  ]);

  // Market adjustment sync
  useFundInvestmentMarketAdjustmentSync({
    isLoading,
    marketAdjustmentFieldAttributes,
    marketAdjustmentForSheet,
    marketComparisonsForSheet,
    marketComparisonsRef,
    prevAsOfDateRef,
    prevMarketCurrencyRef,
    prevMarketDateRef,
    propertyUpdated,
    setCurrentAsOfDate,
    setCurrentPercentileSelection,
    setMarketAdjustmentForSheet,
    setMarketComparisonsForSheet,
  });

  // Higher-Order Function to check cell validation
  const validateAllCells = useCallback(() => () => areCellsValid(false, true) as boolean, [areCellsValid]);

  // Market adjustment layout
  useFundInvestmentLayout({
    isLoading,
    onSave: saveMarketAdjustment,
    pageTitle: FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE,
    showMarketDateOnFilter: true,
    showVersionFilter: true,
    validateAllCells,
  });

  return (
    <CustomContainer className={FUND_INVESTMENTS_CONTAINER_CLASSNAME}>
      <Grid className={FUND_INVESTMENTS_MARKET_ADJUSTMENT_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
        <SectionHeading leftAligned title={FUND_INVESTMENTS_MARKET_ADJUSTMENT_TITLE} />

        {isLoading ? (
          //  Loading grid skeleton while fetching data
          <GridSkeleton maxColumns={7} maxRows={4} />
        ) : (
          <>
            {fundInvestmentMarketAdjustmentSheetProps &&
            fundInvestmentMarketAdjustmentSheet &&
            !isEmpty(marketAdjustmentForSheet) &&
            !isEmpty(marketComparisonsForSheet) ? (
              // Render the scalar spreadsheet with the workbook context
                <WorkbookContext.Provider value={workbookContextValue}>
                  <CustomScalarSpreadsheet
                    {...fundInvestmentMarketAdjustmentSheetProps}
                    isLoading={
                      isFetchingValuationByAllocation ||
                    isFetchingFundInvestmentByAllocation ||
                    isFetchingPublicCompsByDate
                    }
                    isSaving={isUpdatingValuation || isUpdatingFundInvestmentDetails || isValidating}
                  />
                </WorkbookContext.Provider>
              ) : (
              // Display message if no market adjustment and comparisons not synchronized
                areComparisonsSynchronized && (
                  <EmptyTableMessage tableTerms={FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EMPTY_TERMS} />
                )
              )}

            {/* Add Comparable */}
            <AddGpcDialog
              currentAsOfDate={currentAsOfDate}
              currentComps={currentUniqueComparisons}
              currentMarketCurrency={marketCurrency}
              currentMarketDate={marketDate}
              currentMeasurementDate={selectedMeasurementDate}
              currentSetAreThereChanges={setArePendingChanges}
              disabled={isCurrentValuationDisabled || isUpdatingValuation || isUpdatingFundInvestmentDetails}
              onAdd={addComparison}
              openGpcDialog={openAddComparableDialog}
              setOpenGpcDialog={setOpenAddComparableDialog}
              source={FUND_INVESTMENTS_TITLE}
            />

            {/* Notes and documents drawer */}
            {!isEmpty(currentMarketAdjustment) && !isNil(currentMarketAdjustment.id) && (
              <WidgetDrawer
                applicationContext={WidgetDrawerApplicationContext.FUND_INVESTMENT_MARKET_ADJUSTMENT}
                documentReferencesProps={{
                  currentPage: FUND_INVESTMENT_MARKET_ADJUSTMENT_REFERENCE_TITLE,
                  filesToSave,
                  isDisabled: isCurrentValuationDisabled,
                  referencedFeatureId: currentMarketAdjustment.id,
                  referenceType: FUND_INVESTMENT_MARKET_ADJUSTMENT_REFERENCE_TYPE,
                  selectedMeasurementDate,
                  setFilesToSave,
                }}
                notesProps={{
                  isDisabled: isCurrentValuationDisabled,
                  notes,
                  onAddNote,
                  onDeleteNote,
                  onUpdateNotes,
                  pageType: FUND_INVESTMENT_MARKET_ADJUSTMENT_PAGE_VALUE,
                  pageTypeId: currentMarketAdjustment.id,
                  pageTypeKey: FUND_INVESTMENT_MARKET_ADJUSTMENT_PAGE_KEY,
                  setNotes,
                }}
              />
            )}

            {/* Market date dialog */}
            {isMarketDialogOpen && marketDate && (
              <MarketDateDialog
                marketCurrency={marketCurrency}
                marketDate={marketDate}
                onClose={() => setIsMarketDialogOpen(false)}
                onSave={saveMarketDate}
                open
              />
            )}
          </>
        )}
      </Grid>
    </CustomContainer>
  );
};

export default FundInvestmentMarketAdjustment;
